import { byIdsResultJSONZ, parseByIdsResult } from "_shared/_api/by-ids/by-ids-result";
import { api } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import { readDownloadErrorBody } from "documents/_api/document-download-failure";
import { AvatarCrop } from "marketplace/_views/avatars/avatar-crop";
import { AvatarSize } from "marketplace/_views/avatars/avatar-size";
import IndividualSchema, { IndividualJSON } from "marketplace/individuals/_schema/individual-schema";
import { recordUnavailableIndividuals } from "marketplace/individuals/_stores/individual-unavailability-actions";
import { isSettledIndividual } from "marketplace/individuals/individual-resolution";
import UpdateIndividualProfileRequest from "marketplace/individuals/profiles/_api/requests-contracts/update-individual-profile-request";
import IndividualProfile from "marketplace/individuals/profiles/individual-profile";
import { ForumJSON } from "messaging/forums/_schemas/forum-schema";
import Session from "users/__session/session";
import z from "zod";

/**
 * Whether this session is barred from asking the marketplace who a firm's people are.
 *
 * A restricted onboarding session may resolve an identifier it already holds — that is what names
 * the people on its own onboarding proposal, and the API now admits it to the by-id lookups through
 * `MarketplaceDirectoryAccess` (issue #4830). What it may not do is start from a legal entity and
 * be handed back everyone in it: that is enumeration, it is the shape issue #4559 was about, and
 * the API would refuse it anyway because this session holds no member-information access.
 *
 * Deciding it here rather than at each call site is deliberate. Individual call sites used to
 * decide it for themselves, and the ones that forgot, or that read a session snapshot React had
 * not re-rendered yet, asked anyway. Reading the persisted session rather than a React snapshot
 * means the answer is the one that was true when the request was made: agreement acceptance
 * persists the session before it navigates, so a component may still hold the session it rendered
 * with while the persisted copy is already authoritative.
 */
export const marketplaceCannotEnumeratePeople = (): boolean =>
  Session.loadFromStorage(() => {}).isRestrictedOnboarding;

/**
 * The answer of `individuals-by-ids` and `individuals-by-userids`: the people the viewer may see,
 * and, for every other id the request named, whether it is `missing` or `unauthorized`.
 */
const IndividualsByIdsResultJSONZ = byIdsResultJSONZ(IndividualSchema.IndividualJSONZ);
const BY_USER_IDS_REPORT = { schemaName: "IndividualsByIdsResult", operation: "individuals-by-userids" };
const BY_IDS_REPORT = { schemaName: "IndividualsByIdsResult", operation: "individuals-by-ids" };

export const individualsApi = api.injectEndpoints({
  endpoints: (build) => ({
    // #region Get
    getIndividualById: build.query<IndividualJSON, { individualId: string }>({
      query: ({ individualId }) => `marketplace/individuals/${individualId}`,
      rawResponseSchema: IndividualSchema.IndividualJSONZ,
      onSchemaFailure: logSchemaFailure,
      providesTags: (result) =>
        result ? [{ type: "Individual" as const, id: result.id }] : [{ type: "Individual" as const, id: "LIST" }],
    }),
    // An array, not a Set. RTK Query keeps an argument from one render to the next only when it can
    // compare it structurally, and it cannot compare a Set: a Set built during render is a new
    // argument every time, so a failed lookup is sent again on every render (#6488).
    getIndividualsByUserIds: build.query<IndividualJSON[], { userIds: readonly string[] }>({
      async queryFn({ userIds }, { getState, dispatch }, _extraOptions, fetchWithBQ) {
        const state = getState() as { individuals: { byUserId: { entries: Record<string, IndividualJSON> } } };
        const byUserId = state.individuals.byUserId.entries;

        const cachedIndividuals: IndividualJSON[] = [];
        const missingUserIds: string[] = [];
        for (const userId of new Set(userIds)) {
          const cached = byUserId[userId];
          // A contact is asked about again: a shared proposal may since have widened it (#5766).
          if (isSettledIndividual(cached)) cachedIndividuals.push(cached);
          else missingUserIds.push(userId);
        }

        if (!missingUserIds.length) return { data: cachedIndividuals };

        const result = await fetchWithBQ({
          url: "marketplace/individuals-by-userids",
          method: "POST",
          body: missingUserIds,
        });
        if (result.error) return { error: result.error };

        const parsed = parseByIdsResult(IndividualsByIdsResultJSONZ, result.data, BY_USER_IDS_REPORT);
        if ("error" in parsed) return { error: parsed.error };
        dispatch(recordUnavailableIndividuals({ keyedBy: "userId", unavailable: parsed.data.unavailable }));
        return { data: [...cachedIndividuals, ...parsed.data.found] };
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const normalized = [...new Set(queryArgs.userIds)].sort((a, b) => a.localeCompare(b));
        return `${endpointName}(${normalized.join(",")})`;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Individual" as const, id: id })),
              { type: "Individual" as const, id: "LIST" },
            ]
          : [{ type: "Individual" as const, id: "LIST" }],
    }),
    getIndividualVendors: build.query<IndividualJSON[], void>({
      query: () => "marketplace/individual-vendors",
      rawResponseSchema: z.array(IndividualSchema.IndividualJSONZ),
      onSchemaFailure: logSchemaFailure,
      providesTags: [{ type: "Individual" as const, id: "LIST" }],
    }),
    getIndividualsByIds: build.query<IndividualJSON[], { ids: string[] }>({
      async queryFn({ ids }, { dispatch }, _extraOptions, fetchWithBQ) {
        if (!ids.length) return { data: [] };
        const result = await fetchWithBQ({
          url: "marketplace/individuals-by-ids",
          method: "POST",
          body: ids,
          headers: { "Content-Type": "application/json" },
        });
        if (result.error) return { error: result.error };
        const parsed = parseByIdsResult(IndividualsByIdsResultJSONZ, result.data, BY_IDS_REPORT);
        if ("error" in parsed) return { error: parsed.error };
        dispatch(recordUnavailableIndividuals({ keyedBy: "id", unavailable: parsed.data.unavailable }));
        return { data: parsed.data.found };
      },
      responseSchema: z.array(IndividualSchema.IndividualJSONZ),
      onSchemaFailure: logSchemaFailure,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Individual" as const, id: id })),
              { type: "Individual" as const, id: "LIST" },
            ]
          : [{ type: "Individual" as const, id: "LIST" }],
    }),
    getIndividualAvatar: build.query<string, { individualId: string; size?: AvatarSize }>({
      query: ({ individualId, size }) => ({
        url: `marketplace/individuals/${individualId}/avatar`,
        params: size ? { size } : undefined,
      }),
      providesTags: (_result, _error, { individualId }) => [{ type: "Individual" as const, id: individualId }],
    }),
    downloadIndividualResume: build.query<{ blob: Blob; filename?: string }, { individualId: string }>({
      query: ({ individualId }) => ({
        url: `marketplace/individuals/${individualId}/resume`,
        responseHandler: async (response: Response) => {
          // A refusal carries the reason it was refused; see `documentDownloadFailureMessage` (#6994).
          if (!response.ok) return readDownloadErrorBody(response);

          const blob = await response.blob();
          const contentDisposition = response.headers.get("Content-Disposition");
          const matches = contentDisposition
            ? new RegExp(/(?<=filename(?:=|\*=(?:[\w-]+'')))["|']?(?<filename>[^"';\n]+)["|']?/).exec(contentDisposition)
            : null;
          return { blob, filename: matches?.groups?.filename };
        },
      }),
    }),
    // #endregion

    // #region Mutations
    updateIndividual: build.mutation<
      IndividualJSON,
      { individualId: string; isVisible: boolean; originalProfile?: IndividualProfile; updatedProfile?: IndividualProfile }
    >({
      query: ({ individualId, isVisible, originalProfile, updatedProfile }) => ({
        url: `marketplace/individuals/${individualId}`,
        method: "PATCH",
        body: UpdateIndividualProfileRequest.buildPayload(isVisible, originalProfile, updatedProfile),
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      rawResponseSchema: IndividualSchema.IndividualJSONZ,
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
        const { data: individualJSON } = await queryFulfilled;
        if (individualJSON.id) {
          dispatch(
            individualsApi.util.upsertQueryData(
              "getIndividualById",
              { individualId: individualJSON.id },
              individualJSON,
            ),
          );
        }
      },
      invalidatesTags: (_result, _error, { individualId }) => [
        { type: "Individual" as const, id: individualId },
        { type: "Individual" as const, id: "LIST" },
      ],
    }),
    createIndividualAvatar: build.mutation<void, { individualId: string; file: File; crop?: AvatarCrop }>({
      query: ({ individualId, file, crop }) => {
        const formData = new FormData();
        formData.append("file", file);
        if (crop) {
          formData.append("cropX", crop.x.toString());
          formData.append("cropY", crop.y.toString());
          formData.append("cropSize", crop.size.toString());
        }
        return {
          url: `marketplace/individuals/${individualId}/avatar`,
          method: "POST",
          body: formData,
        };
      },
      // The individual's own tag too, so a photo already on screen — the header's, Settings' — is
      // read again. LIST alone left both showing the old one until the page was reloaded.
      invalidatesTags: (_result, _error, { individualId }) => [
        { type: "Individual" as const, id: individualId },
        { type: "Individual" as const, id: "LIST" },
      ],
    }),
    createIndividualResume: build.mutation<void, { individualId: string; file: File }>({
      query: ({ individualId, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `marketplace/individuals/${individualId}/resume`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: () => [{ type: "Individual" as const, id: "LIST" }],
    }),
    deleteIndividualAvatar: build.mutation<void, { individualId: string }>({
      query: ({ individualId }) => ({
        url: `marketplace/individuals/${individualId}/avatar`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { individualId }) => [
        { type: "Individual" as const, id: individualId },
        { type: "Individual" as const, id: "LIST" },
      ],
    }),
    deleteIndividualResume: build.mutation<void, { individualId: string }>({
      query: ({ individualId }) => ({
        url: `marketplace/individuals/${individualId}/resume`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: "Individual" as const, id: "LIST" }],
    }),
    contactIndividual: build.mutation<ForumJSON, { individualId: string }>({
      query: ({ individualId }) => ({
        url: `marketplace/individuals/${individualId}/contact`,
        method: "POST",
        body: {},
      }),
    }),
    // #endregion
  }),
});

export const {
  useGetIndividualByIdQuery,
  useGetIndividualsByUserIdsQuery,
  useGetIndividualVendorsQuery,
  useGetIndividualsByIdsQuery,
  useGetIndividualAvatarQuery,
  useDownloadIndividualResumeQuery,
  useUpdateIndividualMutation,
  useCreateIndividualAvatarMutation,
  useCreateIndividualResumeMutation,
  useDeleteIndividualAvatarMutation,
  useDeleteIndividualResumeMutation,
  useContactIndividualMutation,
} = individualsApi;
